import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { Config } from 'ionic-angular';

import { GuestModel } from '../../app/models/guest-model';
import { TopicModel } from '../../app/models/topic-model';
import { TopicMessageModel } from '../../app/models/topic-message-model';

import { ForumService } from '../../providers/forum-service/forum-service';

@Injectable()
export class RestForumService implements ForumService {
  private serverUrl : string;

  constructor(private http: Http, configuration: Config) {
    this.serverUrl = configuration.get('backendUrl');
  }

  renameTopic(eventCode: string, topicKey: string, topicTitle: string) : Observable<boolean> {
    return Observable.create(observer => {
      this.http.put(this.serverUrl+'/api/'+eventCode+'/topicTitle/'+topicKey,{title: topicTitle})
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  modifyMessageContent(eventCode: string, topicKey: string, messageKey: string, messageContent: string) : Observable<boolean> {
    //console.log("modifyMessageContent", messageKey);
    return Observable.create(observer => {
      this.http.put(this.serverUrl+'/api/'+eventCode+'/topic/'+topicKey+'/message/'+messageKey,{content: messageContent})
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  removeTopic(topic : TopicModel) : Observable<boolean> {
    return Observable.create(observer => {
      this.http.delete(this.serverUrl+'/api/'+topic.eventCode+'/topic/'+topic.key)
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  removeMessage(message: TopicMessageModel) : Observable<boolean> {
    return Observable.create(observer => {
      this.http.delete(this.serverUrl+'/api/'+message.eventCode+'/topic/'+message.topicKey+'/message/'+message.key)
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  createTopic(topic : TopicModel) : Observable<boolean> {
    return Observable.create(observer => {
      this.http.post(this.serverUrl+'/api/'+topic.eventCode+'/topic',this.adaptToRemoteTopic(topic, new Array<TopicMessageModel>(topic.firstMessage)))
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  addMessage(topic: TopicModel, message: TopicMessageModel) : Observable<boolean> {
    return Observable.create(observer => {
      this.http.post(this.serverUrl+'/api/'+topic.eventCode+'/topic/'+topic.key+'/message',this.adaptToRemoteTopicMessage(message))
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(true);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(false);
        });
    });
  }

  findMessagesForTopic(topicKey: string, eventCode : string) : Observable<TopicMessageModel[]> {
    return Observable.create(observer => {
      this.http.get(this.serverUrl+'/api/'+eventCode+'/topic/'+topicKey)
        .map(res => this.adaptFromRemoteTopicMessages(res.json()))
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(data);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(error);
        });
    });
  }

  public findTopic(topicKey: string, eventCode : string) : Observable<TopicModel> {
    return Observable.create(observer => {
      this.http.get(this.serverUrl+'/api/'+eventCode+'/topic/'+topicKey)
        .map(res => this.adaptFromRemoteTopic(res.json()))
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(data);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(error);
        });
    });
  }

  public findTopicsForEvent(eventCode : string) : Observable<TopicModel[]> {
    return Observable.create(observer => {
      this.http.get(this.serverUrl+'/api/'+eventCode+'/topics/')
        .map(res => this.adaptFromRemoteTopics(res.json()))
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(data);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(error);
        });
    });
  }

  private adaptFromRemoteTopics(topics: Array<any>) : Array<TopicModel> {
    return topics.map(topic => this.adaptFromRemoteTopic(topic));
	}

  private adaptFromRemoteTopic(topic: any) : TopicModel {
    let numberOfMessages = topic.messages.length;
    return new TopicModel(topic.eventCode, topic.key, topic.title, numberOfMessages <= 0 ? null : this.adaptFromRemoteTopicMessage(topic.eventCode, topic.key, topic.messages[0]), numberOfMessages <= 0 ? null : this.adaptFromRemoteTopicMessage(topic.eventCode, topic.key, topic.messages[numberOfMessages - 1]), numberOfMessages);
	}

  private adaptToRemoteTopic(topic: TopicModel, messages: Array<TopicMessageModel>) : any {
    let dbTopic = {
      key: topic.key,
      eventCode: topic.eventCode,
      title: topic.title,
      messages: messages.map(message => this.adaptToRemoteTopicMessage(message))
    };
    return dbTopic;
	}

  private adaptToRemoteTopicMessage(message: TopicMessageModel) : any {
    //console.log("adaptToRemoteTopicMessage IN",message);
    let dbMessage = {
      content: message.content,
      key: message.key,
      author: message.authorId,
      creationDate: message.creationDate,
      modificationDate: message.modificationDate
    };
    //console.log("adaptToRemoteTopicMessage OUT",dbMessage);
    return dbMessage;
	}

  private adaptFromRemoteTopicMessages(topic: any) : Array<TopicMessageModel> {
    return topic.messages.map(message => this.adaptFromRemoteTopicMessage(topic.eventCode, topic.key, message));
	}

  private adaptFromRemoteTopicMessage(eventCode: string, topicKey: string, message: any) : TopicMessageModel {
    //console.log("adaptFromRemoteTopicMessage IN", message);
    let mess = new TopicMessageModel(eventCode, topicKey, message.key, message.content, message.author, message.creationDate, message.modificationDate);
    //console.log("adaptFromRemoteTopicMessage OUT", mess);
    return mess;
	}

  visitTopic(guest : GuestModel, topic : TopicModel) : Observable<Date> {
    return Observable.create(observer => {
      this.http.put(this.serverUrl+'/api/'+topic.eventCode+'/guest/'+guest.getId()+'/visitTopic/'+topic.key, {})
        .catch(this.handleError)
        .subscribe(data => {
          observer.next(data.visitDate);
          observer.complete();
        },
        error => {
          this.handleError(error);
          observer.next(undefined);
        });
    });
  }

  private handleError(error) {
    console.error(error);
    return Observable.throw((error.status === 404 ? error.text() : error.json().error) || 'Server error while reading topics');
  }

}




// WEBPACK FOOTER //
// ./src/providers/forum-service/rest-forum-service.ts